import { observer } from 'mobx-react-lite';
import { DeliverectLogState, orderStatusI18n } from './deliverect-log-state';
import i18n from '../../i18n';
import { Box, Chip, Grid } from '@mui/material';
import CommonInput from '../../component/form/common-input.component';
import PeriodDateFilter from '../../component/period-date-filter.component';
import { Option } from '../../component/form/select-input-multiple';
import TableComponent, { HeadCell } from '../../component/table/table.component';
import './scss/deliverect-log.style.scss';
import SearchComponent from '../../component/form/search-input.component';
import moment from 'moment';
import { DeliverectLogDetail } from '../../models/deliverect-log-detail';
import { PopoverEllipsis } from '../../component/popover/popover-ellipsis';

/**
 * Deliverect平台相关日志页
 */
const DeliverectPage = observer(({ useData }: { useData: DeliverectLogState }) => {
    const {
        isTableLoading,
        opTargetSelectValue,
        searchNameSelectValue,
        logTypeSelectValue,
        pickerValue,
        logLists,
    } = useData;

    // *********************
    // 搜索筛选数据
    // *********************
    // 操作对象筛选数组  下拉框选项
    const op_target_selections: Option[] = [
        // 全部项目
        { value: 'all', label: i18n.t('deliverect_log_page_text_all_items') },
        // 2.0项目
        { value: '2.0', label: `2.0` },
        // Deliverect平台
        { value: 'DELIVERECT', label: 'DELIVERECT' }
    ];
    // 餐厅名称 | 集团名称 筛选数组  下拉框
    const search_name_selections: Option[] = [
        // 集团名称
        { value: 'group_nickname', label: i18n.t('group_management_page_text_group_name') },
        // 餐厅名称
        { value: 'restaurant_nickname', label: i18n.t('restaurant_management_page_table_text_restaurant_name') },
    ];
    // 日志类型筛选数组  下拉框选项 '0' | '1' | '2'
    const log_type_selections: Option[] = [
        // 所有日志数据
        { value: 'all', label: i18n.t('deliverect_log_page_text_all_logs') },
        // 订单日志
        { value: '0', label: i18n.t('deliverect_log_page_text_order_logs') },
        // 菜品日志
        { value: '1', label: i18n.t('deliverect_log_page_text_food_journal') },
        // 错误日志
        { value: '2', label: i18n.t('deliverect_log_page_text_error_log') },
    ];
    // 表格头部数据
    const table_head_cell: HeadCell[] = [
        // 集团
        { id: 'group_nickname', label: i18n.t('group_management_page_text_group'), sortStatus: useData.getSortStatus('group_nickname') },
        // 餐厅
        { id: 'restaurant_nickname', label: i18n.t('group_management_page_text_restaurant'), sortStatus: useData.getSortStatus('restaurant_nickname') },
        // 操作对象 2.0项目、Deliveract平台
        {
            id: 'operator', label: i18n.t('deliverect_log_page_text_operand'), sortStatus: -1
        },
        // 日志类型 type: '0' | '1' | '2'  订单 菜品 错误
        {
            id: 'type', label: i18n.t('deliverect_log_page_text_log_type'), sortStatus: -1, renderFunction: (item) => {
                switch (item.type) {
                    case '0':
                        return i18n.t('deliverect_log_page_text_order_logs');
                    case '1':
                        return i18n.t('deliverect_log_page_text_food_journal');
                    case '2':
                        return i18n.t('deliverect_log_page_text_error_log');
                    default:
                        return ''
                }
            }
        },
        // 操作时间
        {
            id: 'date', label: i18n.t('operation_record_operation_time'), sortStatus: -1, renderFunction: (item) => {
                return moment(item.date).format('YYYY-MM-DD HH:mm:ss')
            }
        },
        // 日志状态
        {
            id: 'log_status', width: '30%', label: i18n.t('deliverect_log_page_text_log_status'), sortStatus: -1, renderFunction: (item) => {
                const ChipDom = (props: { label: string, type: 'success' | 'error' }) => {
                    return <Chip size='small' color={props.type} sx={() => { return { '.MuiChip-label': { color: '#fff' } } }} label={props.label} />;
                }
                switch (item.type) {
                    case '0':
                    case '1':
                        return <ChipDom label={i18n.t('general_text_success')} type={'success'} />;
                    case '2':
                        return <ChipDom label={i18n.t('general_text_failed')} type={'error'} />;
                    default:
                        return ''
                }
            }
        },
        // 日志详情
        {
            id: 'log_detail', label: i18n.t('deliverect_log_page_text_log_details'), sortStatus: -1, renderFunction: (item) => {
                return logDetail(item)
            }
        },
    ];

    /**
     * 日志详情
     * - 订单、菜品、错误
     */
    const logDetail = (item: DeliverectLogDetail) => {
        let text: string | JSX.Element = '';
        switch (item.type) {
            //  订单
            case '0':
                text = `${i18n.t('business_analysis_page_order_account')}:${item?.detail?.orderId}; ${i18n.t('menu_and_dishes_page_price')}:${item?.detail?.order_price}; ${i18n.t('deliverect_log_page_text_log_order_status')}:${orderStatusI18n(item?.detail?.order_status!)}; `
                break;
            //  菜品
            case '1':
                // 菜品同步详情
                text = reportView(item.detail, item.operator!);
                break;
            //  错误
            case '2':
                text = JSON.stringify(item.detail);
                break;
            default:
                break;
        }
        return <PopoverEllipsis showData={text} className='DeliverectLogTableCell' />
    }

    /**
     * 同步菜品报告
     * @param reportDetails
     * @param type
     * @returns
     */
    const reportView = (reportDetails: DeliverectLogDetail['detail'], operator: string) => {
        switch (operator) {
            case 'DELIVERECT':
            case 'Deliverect':
                return (
                    <div className='reportDetails'  >
                        <p className='text'>
                            {`${i18n.t('menu_and_dishes_page_normal_dishes')}:${reportDetails?.dish?.insertedCount || 0};`}
                        </p>
                        <p className='text'>
                            {`${i18n.t('menu_and_dishes_page_set_meal')}:${reportDetails?.dish?.insertedCount || 0};`}
                        </p>
                        <p className='text'>
                            {`${i18n.t('synchronize_Deliverect_page_setmeal_area')}:${reportDetails?.dish?.insertedCount || 0};`}
                        </p>
                        <p className='text'>
                            {`${i18n.t('synchronize_Deliverect_page_condiment')}:${reportDetails?.Condiment?.insertedCount || 0};`}
                        </p>
                        <p className='text'>
                            {`${i18n.t('synchronize_Deliverect_page_condiment_group')}:${reportDetails?.CondimentGroup?.insertedCount || 0};`}
                        </p>
                    </div>
                )
            default: {
                // 菜品、套餐
                const productsInserted = reportDetails?.products?.inserted || 0;
                const productsDelted = reportDetails?.products?.deleted || 0;
                const productsUpdated = reportDetails?.products?.updated || 0;
                const productsIgnored = reportDetails?.products?.ignored || 0;
                // 套餐区域
                const bundlesInserted = reportDetails?.bundles?.inserted || 0;
                const bundlesDelted = reportDetails?.bundles?.deleted || 0;
                const bundlesUpdated = reportDetails?.bundles?.updated || 0;
                const bundlesIgnored = reportDetails?.bundles?.ignored || 0;
                // 调味品
                const modifierInserted = reportDetails?.modifiers?.inserted || 0;
                const modifierDelted = reportDetails?.modifiers?.deleted || 0;
                const modifierUpdated = reportDetails?.modifiers?.updated || 0;
                const modifierIgnored = reportDetails?.modifiers?.ignored || 0;
                // 调味品组
                const modifierGroupsInserted = reportDetails?.modifierGroups?.inserted || 0;
                const modifierGroupsDelted = reportDetails?.modifierGroups?.deleted || 0;
                const modifierGroupsUpdated = reportDetails?.modifierGroups?.updated || 0;
                const modifierGroupsIgnored = reportDetails?.modifierGroups?.ignored || 0;

                return (
                    <div className='reportDetails'>
                        <p className='text'>
                            {`
                            ${i18n.t('menu_and_dishes_page_normal_dishes')}、${i18n.t('menu_and_dishes_page_set_meal')}:
                            ${`${productsInserted || 0} ${i18n.t('synchronize_Deliverect_page_inserted')};`}
                            ${`${productsDelted || 0} ${i18n.t('synchronize_Deliverect_page_deleted')};`}
                            ${`${productsUpdated || 0} ${i18n.t('synchronize_Deliverect_page_updated')};`}
                            ${`${productsIgnored || 0} ${i18n.t('synchronize_Deliverect_page_ignored')};`}
                            `}
                        </p>
                        <p className='text'>
                            {`
                            ${i18n.t('synchronize_Deliverect_page_setmeal_area')}:
                            ${`${bundlesInserted || 0} ${i18n.t('synchronize_Deliverect_page_inserted')};`}
                            ${`${bundlesDelted || 0} ${i18n.t('synchronize_Deliverect_page_deleted')};`}
                            ${`${bundlesUpdated || 0} ${i18n.t('synchronize_Deliverect_page_updated')};`}
                            ${`${bundlesIgnored || 0} ${i18n.t('synchronize_Deliverect_page_ignored')};`}
                            `}
                        </p>
                        <p className='text'>
                            {`
                            ${i18n.t('synchronize_Deliverect_page_condiment')}:
                            ${`${modifierInserted || 0} ${i18n.t('synchronize_Deliverect_page_inserted')};`}
                            ${`${modifierDelted || 0} ${i18n.t('synchronize_Deliverect_page_deleted')};`}
                            ${`${modifierUpdated || 0} ${i18n.t('synchronize_Deliverect_page_updated')};`}
                            ${`${modifierIgnored || 0} ${i18n.t('synchronize_Deliverect_page_ignored')};`}
                            `}
                        </p>
                        <p className='text'>
                            {`
                            ${i18n.t('synchronize_Deliverect_page_condiment_group')}:
                            ${`${modifierGroupsInserted || 0} ${i18n.t('synchronize_Deliverect_page_inserted')};`}
                            ${`${modifierGroupsDelted || 0} ${i18n.t('synchronize_Deliverect_page_deleted')};`}
                            ${`${modifierGroupsUpdated || 0} ${i18n.t('synchronize_Deliverect_page_updated')};`}
                            ${`${modifierGroupsIgnored || 0} ${i18n.t('synchronize_Deliverect_page_ignored')};`}
                            `}
                        </p>
                    </div>
                )
            }
        }

    }

    // *********************
    // View Controller
    // *********************
    
    return (
        <Box className={'container'}>
            <Grid container>
                <Grid item xs={12}>
                    {/* 顶部工具栏 */}
                    <Box className={'table-top-tool'}>
                        <Box className={'select-input'}>
                            <CommonInput
                                inputType={'SELECT'}
                                select={true}
                                items={op_target_selections}
                                size={'small'}
                                fullWidth
                                label={i18n.t('deliverect_log_page_text_operand')}
                                value={opTargetSelectValue}
                                onChange={(e) => {
                                    const selectVal = e.target.value;
                                    useData.setOpTargetSelectValue(selectVal);
                                }}
                            />
                        </Box>
                        <Box className={'select-input'}>
                            <CommonInput
                                inputType={'SELECT'}
                                select={true}
                                items={log_type_selections}
                                size={'small'}
                                fullWidth
                                label={i18n.t('deliverect_log_page_text_log_type')}
                                value={logTypeSelectValue}
                                onChange={(e) => {
                                    const selectVal = e.target.value;
                                    useData.setLogTypeSelectValue(selectVal);
                                }}
                            />
                        </Box>
                        <Box className={'select-input'}>
                            <CommonInput
                                inputType={'SELECT'}
                                select={true}
                                items={search_name_selections}
                                size={'small'}
                                fullWidth
                                label={i18n.t('operation_record_search_condition')}
                                value={searchNameSelectValue}
                                onChange={(e) => {
                                    const selectVal = e.target.value;
                                    useData.setSearchNameSelectValue(selectVal);
                                }}
                            />
                        </Box>
                        <SearchComponent
                            {...{
                                allowClear: true,
                                onSearch: () => {
                                    // TODO 搜索待补充
                                    useData.handleSearch();
                                },
                                onChange: (value) => {
                                    useData.updateSearchValue(value);
                                }
                            }}
                        />
                        <Box sx={{ marginLeft: 2 }}>
                            <PeriodDateFilter
                                startDate={useData.sendDateTimeData.startDate}
                                endDate={useData.sendDateTimeData.endDate}
                                pickerValue={pickerValue}
                                onDateTimeChange={(dates) => useData.handleDateTime(dates)}
                            />
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    {/* 表格 */}
                    <TableComponent
                        {...{
                            showChecked: 0,
                            list: logLists,
                            // 开启表格自定义页脚
                            isUseCustomPagination: true,
                            headCellList: table_head_cell,
                            isTableLoading,
                            handleSort: (headCell) => {
                                useData.handleSort(headCell);
                            },
                            isPagination: true,
                            allCount: logLists.length,
                            isUseFold: true,
                            extraClass: 'extraTable',
                        }}
                    />
                </Grid>
            </Grid>
        </Box>
    )
});

export default DeliverectPage;